/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Grid,
  Paper,
  Alert,
} from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../services/api';

const OrderConfirmationPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [order, setOrder] = useState<any>(null);
  const [orders, setOrders] = useState<any[]>([]); // Multiple orders for multi-shop checkout
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [previousStatus, setPreviousStatus] = useState<string | null>(null);
  const [statusChangeNotification, setStatusChangeNotification] = useState<string | null>(null);
  const [failedOrders, setFailedOrders] = useState<Array<{ shop: any; error: string }>>([]);

  // Get order data from location state if available (from checkout)
  const orderData = location.state?.order;
  const ordersData = location.state?.orders; // Multiple orders
  const failedOrdersData = location.state?.failedOrders; // Failed orders

  useEffect(() => {
    if (ordersData && Array.isArray(ordersData) && ordersData.length > 0) {
      // Multiple orders from multi-shop checkout
      console.log('🛒 OrderConfirmationPage: Received multiple orders:', ordersData);
      setOrders(ordersData);
      setOrder(ordersData[0]); // Set first order as primary for compatibility
      setPreviousStatus(ordersData[0]?.status);
      if (failedOrdersData) {
        setFailedOrders(failedOrdersData);
      }
      setLoading(false);
    } else if (orderData) {
      // Single order
      setOrder(orderData);
      setOrders([orderData]);
      setPreviousStatus(orderData.status);
      setLoading(false);
    } else if (orderId) {
      loadOrderDetails();
    }
  }, [orderId, orderData, ordersData, failedOrdersData]);

  // Auto-refresh disabled - order confirmation will only refresh when user manually clicks refresh
  // useEffect(() => {
  //   if (!orderId) return;

  //   const interval = setInterval(() => {
  //     loadOrderDetails();
  //   }, 10000); // Refresh every 10 seconds

  //   return () => clearInterval(interval);
  // }, [orderId]);

  // Check for status changes and show notification
  useEffect(() => {
    if (order && lastUpdated) {
      const statusElement = document.querySelector('[data-order-status]');
      if (statusElement) {
        statusElement.classList.add('status-updated');
        setTimeout(() => {
          statusElement.classList.remove('status-updated');
        }, 2000);
      }
    }
  }, [order?.status, lastUpdated]);

  const loadOrderDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🛒 OrderConfirmationPage: Loading order details for:', orderId);
      
      const orderData = await apiService.getOrder(orderId!);
      console.log('✅ OrderConfirmationPage: Order data received:', orderData);
      
      if (!orderData || (!orderData.id && !orderData.order_id)) {
        console.error('❌ OrderConfirmationPage: Invalid order data received:', orderData);
        setError('Order data is invalid. The order may not have been saved properly.');
        return;
      }
      
      // Check if status changed
      if (previousStatus && previousStatus !== orderData.status) {
        console.log('Status changed from', previousStatus, 'to', orderData.status);
        setStatusChangeNotification(`Order status updated from ${previousStatus} to ${orderData.status}`);
        setTimeout(() => setStatusChangeNotification(null), 5000);
      }
      
      setPreviousStatus(orderData.status);
      setOrder(orderData);
      setLastUpdated(new Date());
    } catch (error: any) {
      console.error('❌ OrderConfirmationPage: Error loading order details:', error);
      
      // Provide more specific error messages
      if (error?.message?.includes('not found') || error?.message?.includes('404')) {
        setError(`Order not found. The order with ID ${orderId} may not have been saved to the database. Please check with support.`);
      } else if (error?.message?.includes('Network')) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(error?.message || 'Failed to load order details. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'confirmed':
        return 'info';
      case 'processing':
        return 'secondary';
      case 'shipped':
        return 'primary';
      case 'delivered':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return '⏳';
      case 'confirmed':
        return '✅';
      case 'processing':
        return '⚙️';
      case 'shipped':
        return '🚚';
      case 'delivered':
        return '📦';
      case 'cancelled':
        return '❌';
      default:
        return '❓';
    }
  };

  const getEstimatedDelivery = () => {
    const today = new Date();
    const deliveryDate = new Date(today);
    deliveryDate.setDate(today.getDate() + 3); // Default 3 days
    return deliveryDate.toLocaleDateString();
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h6">Loading order confirmation...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={loadOrderDetails}>
          Retry
        </Button>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>
          Order Not Found
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          The order you're looking for doesn't exist or has been removed.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/orders')}>
          View All Orders
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Success Message */}
      <Box textAlign="center" mb={4}>
        <Typography variant="h3" color="success.main" gutterBottom>
          🎉 {orders.length > 1 ? `${orders.length} Orders` : 'Order'} Placed Successfully!
        </Typography>
        <Typography variant="h5" gutterBottom>
          Thank you for your {orders.length > 1 ? 'orders' : 'order'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {orders.length > 1 
            ? `We've received ${orders.length} orders from different shops and will send you confirmation emails shortly.`
            : "We've received your order and will send you a confirmation email shortly."}
        </Typography>
      </Box>

      {/* Failed Orders Warning */}
      {failedOrders.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Some orders could not be placed:
          </Typography>
          {failedOrders.map((fo, idx) => (
            <Typography key={idx} variant="body2">
              • {fo.shop.name}: {fo.error}
            </Typography>
          ))}
        </Alert>
      )}

      {/* Status Change Notification */}
      {statusChangeNotification && (
        <Alert 
          severity="success" 
          sx={{ mb: 2 }}
          onClose={() => setStatusChangeNotification(null)}
        >
          {statusChangeNotification}
        </Alert>
      )}

      {/* Multiple Orders Summary */}
      {orders.length > 1 && (
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            Orders Summary ({orders.length} orders)
          </Typography>
          <Grid container spacing={2}>
            {orders.map((ord: any, idx: number) => (
              <Grid item xs={12} sm={6} key={ord.id || idx}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Order #{ord.order_number || ord.id} - {ord.shop?.name || 'Shop'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {ord.items?.length || 0} item(s) • ₹{ord.total_amount?.toFixed(2) || '0.00'}
                    </Typography>
                    <Chip
                      label={ord.status?.toUpperCase() || 'PENDING'}
                      color={getStatusColor(ord.status || 'pending')}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Shop-wise Order Breakdown */}
      {orders.length > 1 ? (
        <Grid container spacing={3}>
          {orders.map((ord: any, orderIndex: number) => (
            <Grid item xs={12} key={ord.id || orderIndex}>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                    <Box display="flex" alignItems="center">
                      <Typography variant="h6" sx={{ mr: 2 }}>
                        {getStatusIcon(ord.status)}
                      </Typography>
                      <Box>
                        <Typography variant="h6">
                          Order #{ord.order_number || ord.id} - {ord.shop?.name || 'Shop'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Placed on {new Date(ord.created_at || Date.now()).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={ord.status?.toUpperCase() || 'PENDING'}
                      color={getStatusColor(ord.status || 'pending')}
                      variant="outlined"
                    />
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Order Items for this shop */}
                  <Typography variant="h6" gutterBottom>
                    Items from {ord.shop?.name || 'Shop'}
                  </Typography>
                  <List>
                    {ord.items?.map((item: any, index: number) => (
                      <ListItem key={index} divider>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            📦
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={item.product_name || 'Product'}
                          secondary={`Quantity: ${item.quantity} × ₹${item.unit_price}`}
                        />
                        <Typography variant="subtitle1" fontWeight="bold">
                          ₹{(item.quantity * item.unit_price).toFixed(2)}
                        </Typography>
                      </ListItem>
                    ))}
                  </List>

                  <Divider sx={{ my: 2 }} />

                  {/* Order Summary for this shop */}
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Subtotal</Typography>
                    <Typography variant="body2">₹{ord.subtotal?.toFixed(2) || '0.00'}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Shipping</Typography>
                    <Typography variant="body2">₹{ord.shipping_amount?.toFixed(2) || '0.00'}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={2}>
                    <Typography variant="body2">Tax</Typography>
                    <Typography variant="body2">₹{ord.tax_amount?.toFixed(2) || '0.00'}</Typography>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="h6" fontWeight="bold">
                      Total
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      ₹{ord.total_amount?.toFixed(2) || '0.00'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
          
          {/* Sidebar for multiple orders */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
              <Typography variant="h6" gutterBottom>
                All Orders Summary
              </Typography>
              <Divider sx={{ my: 2 }} />
              
              {/* Show breakdown for each order */}
              {orders.map((ord: any, idx: number) => (
                <Box key={ord.id || idx} mb={2} pb={2} borderBottom={idx < orders.length - 1 ? '1px solid #e5e7eb' : 'none'}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    {ord.shop?.name || 'Shop'} - Order #{ord.order_number || ord.id}
                  </Typography>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" fontSize="0.75rem">{ord.items?.length || 0} item(s)</Typography>
                    <Typography variant="body2" fontSize="0.75rem">₹{ord.total_amount?.toFixed(2) || '0.00'}</Typography>
                  </Box>
                </Box>
              ))}
              <Divider sx={{ my: 2 }} />
              {/* Grand Total */}
              <Box display="flex" justifyContent="space-between" mb={3}>
                <Typography variant="h6" fontWeight="bold">
                  Grand Total
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  ₹{orders.reduce((sum, ord) => sum + (ord.total_amount || 0), 0).toFixed(2)}
                </Typography>
              </Box>

              <Box display="flex" flexDirection="column" gap={2}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<span>📧</span>}
                  onClick={() => {
                    alert('Confirmation emails sent for all orders!');
                  }}
                >
                  Send Confirmation Emails
                </Button>
                
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<span>📋</span>}
                  onClick={() => navigate('/orders')}
                >
                  View All Orders
                </Button>
                
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<span>🔄</span>}
                  onClick={() => navigate('/')}
                >
                  Continue Shopping
                </Button>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Customer Support */}
              <Box textAlign="center">
                <Typography variant="subtitle2" gutterBottom>
                  Need Help?
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Contact our customer support team
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    window.open('mailto:support@homeneeds.com');
                  }}
                >
                  📞 Contact Support
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={3}>
          {/* Single Order Summary */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                  <Box display="flex" alignItems="center">
                    <Typography variant="h6" sx={{ mr: 2 }}>
                      {getStatusIcon(order.status)}
                    </Typography>
                    <Box>
                      <Typography variant="h6">
                        Order #{order.order_number || order.id}
                      </Typography>
                      {order.shop?.name && (
                        <Typography variant="body2" color="text.secondary">
                          From: {order.shop.name}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Chip
                      label={order.status?.toUpperCase() || 'PENDING'}
                      color={getStatusColor(order.status || 'pending')}
                      variant="outlined"
                      data-order-status
                      sx={{
                        transition: 'all 0.3s ease',
                        '&.status-updated': {
                          transform: 'scale(1.1)',
                          boxShadow: '0 0 10px rgba(25, 118, 210, 0.5)',
                        }
                      }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={loadOrderDetails}
                      disabled={loading}
                      startIcon={loading ? <span>⏳</span> : <span>🔄</span>}
                    >
                      Refresh
                    </Button>
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Placed on {new Date(order.created_at || Date.now()).toLocaleDateString()}
                  {lastUpdated && (
                    <span> • Last updated: {lastUpdated.toLocaleTimeString()}</span>
                  )}
                </Typography>

                <Divider sx={{ my: 3 }} />

                {/* Order Items */}
                <Typography variant="h6" gutterBottom>
                  Order Items
                </Typography>
                <List>
                  {order.items?.map((item: any, index: number) => (
                    <ListItem key={index} divider>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          📦
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={item.product_name || 'Product'}
                        secondary={`Quantity: ${item.quantity} × ₹${item.unit_price}`}
                      />
                      <Typography variant="subtitle1" fontWeight="bold">
                        ₹{(item.quantity * item.unit_price).toFixed(2)}
                      </Typography>
                    </ListItem>
                  ))}
                </List>

                <Divider sx={{ my: 3 }} />

                {/* Delivery Information */}
                <Typography variant="h6" gutterBottom>
                  Delivery Information
                </Typography>
                
                <Box mb={2}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    📍 Delivery Address
                  </Typography>
                  <Typography variant="body2">
                    {order.shipping_address?.street || 'Address not available'}
                  </Typography>
                  <Typography variant="body2">
                    {order.shipping_address?.city}, {order.shipping_address?.state} - {order.shipping_address?.pincode}
                  </Typography>
                </Box>

                <Box mb={2}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    💳 Payment Method
                  </Typography>
                  <Typography variant="body2">
                    {order.payment_method || 'Payment method not specified'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    📅 Estimated Delivery
                  </Typography>
                  <Typography variant="body2">
                    {getEstimatedDelivery()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

        {/* Order Summary & Actions */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
            <Typography variant="h6" gutterBottom>
              {orders.length > 1 ? 'All Orders Summary' : 'Order Summary'}
            </Typography>
            <Divider sx={{ my: 2 }} />
            
            {orders.length > 1 ? (
              <>
                {/* Show breakdown for each order */}
                {orders.map((ord: any, idx: number) => (
                  <Box key={ord.id || idx} mb={2} pb={2} borderBottom={idx < orders.length - 1 ? '1px solid #e5e7eb' : 'none'}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      {ord.shop?.name || 'Shop'} - Order #{ord.order_number || ord.id}
                    </Typography>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" fontSize="0.75rem">Items</Typography>
                      <Typography variant="body2" fontSize="0.75rem">₹{ord.total_amount?.toFixed(2) || '0.00'}</Typography>
                    </Box>
                  </Box>
                ))}
                <Divider sx={{ my: 2 }} />
                {/* Grand Total */}
                <Box display="flex" justifyContent="space-between" mb={3}>
                  <Typography variant="h6" fontWeight="bold">
                    Grand Total
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    ₹{orders.reduce((sum, ord) => sum + (ord.total_amount || 0), 0).toFixed(2)}
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Subtotal</Typography>
                  <Typography variant="body2">₹{order.subtotal?.toFixed(2) || '0.00'}</Typography>
                </Box>
                
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Shipping</Typography>
                  <Typography variant="body2">₹{order.shipping_amount?.toFixed(2) || '0.00'}</Typography>
                </Box>
                
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="body2">Tax</Typography>
                  <Typography variant="body2">₹{order.tax_amount?.toFixed(2) || '0.00'}</Typography>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Box display="flex" justifyContent="space-between" mb={3}>
                  <Typography variant="h6" fontWeight="bold">
                    Total
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    ₹{order.total_amount?.toFixed(2) || '0.00'}
                  </Typography>
                </Box>
              </>
            )}

            <Box display="flex" flexDirection="column" gap={2}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<span>📧</span>}
                onClick={() => {
                  // Send confirmation email
                  alert('Confirmation email sent!');
                }}
              >
                Send Confirmation Email
              </Button>
              
              {orders.length > 1 ? (
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<span>📋</span>}
                  onClick={() => navigate('/orders')}
                >
                  View All Orders
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<span>🚚</span>}
                  onClick={() => navigate(`/orders/${order.id}`)}
                >
                  Track Order
                </Button>
              )}
              
              <Button
                variant="outlined"
                fullWidth
                startIcon={<span>📋</span>}
                onClick={() => navigate('/orders')}
              >
                View All Orders
              </Button>
              
              <Button
                variant="outlined"
                fullWidth
                startIcon={<span>🔄</span>}
                onClick={() => navigate('/')}
              >
                Continue Shopping
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Customer Support */}
            <Box textAlign="center">
              <Typography variant="subtitle2" gutterBottom>
                Need Help?
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Contact our customer support team
              </Typography>
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  // Open support chat or email
                  window.open('mailto:support@homeneeds.com');
                }}
              >
                📞 Contact Support
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      )}
    </Container>
  );
};

export default OrderConfirmationPage;


